import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { matchPath } from 'react-router';
import {
  changeRefererAfterSuccessAuthentication,
  clearRefererAfterSuccessAuthentication
} from 'redux/actions/users/authenticationRefererActions';
import { resetUserGuestStateAuthentication } from 'redux/actions/users/clearUserGuestStateActions';
import { isAuthed, isGuest, getRefererUrlAfterAuthentication } from 'redux/query';
import { linkHandler } from 'utils/linkHandler';
import { useRouteData } from 'Application/RouteDataProvider/RouteDataProvider';

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export const AuthenticationContainer = () => {
  const { currentRoute } = useRouteData();

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const state = location.state;

  const authed = useSelector(isAuthed);
  const guest = useSelector(isGuest);
  const refererUrl = useSelector(getRefererUrlAfterAuthentication);

  const allowedUserSignInRoutes = useMemo(
    () => [
      linkHandler.get('signup'),
      linkHandler.get('confirmMailCode'),
      linkHandler.get('confirmMail'),
      linkHandler.get('fallbackAppOtp'),
      linkHandler.get('login')
    ],
    []
  );

  const resetUserGuestStateRoutes = useMemo(() => [linkHandler.get('signup'), linkHandler.get('login')], []);

  const hasAllowedPath = allowedUserSignInRoutes.some((path) => matchPath(location.pathname, { path, exact: true, strict: false }));

  const isResetRoute = resetUserGuestStateRoutes.some((path) => location.pathname.length === path.length);

  const prev = usePrevious({ authed, guest, isResetRoute });

  useEffect(() => {
    if (currentRoute?.disableAuthentication) return;

    // store referer from `state.from`
    if ((!authed || guest) && state?.from?.pathname && state.from.pathname !== refererUrl) {
      dispatch(changeRefererAfterSuccessAuthentication(state.from.pathname));
    }

    // clear referer if we're not on an allowed path
    if (refererUrl !== linkHandler.get('profilePersonalData') && !hasAllowedPath) {
      dispatch(clearRefererAfterSuccessAuthentication());
    }

    // redirect authenticated user
    if (authed && prev?.authed === false && prev?.guest === false && !guest) {
      dispatch(clearRefererAfterSuccessAuthentication());
      history.push(refererUrl);
    }

    // redirect to OTP screen
    if (!authed && !guest && prev?.authed && prev?.guest) {
      const target = window.innerWidth <= 760 ? `${linkHandler.get('confirmMail')}#sign-in-user-with-otp` : linkHandler.get('confirmMail');
      history.push(target);
    }

    // reset guest auth state
    if (!authed && !guest && !prev?.isResetRoute && isResetRoute) {
      dispatch(resetUserGuestStateAuthentication());
    }
  }, [authed, guest, refererUrl, hasAllowedPath, isResetRoute, state, dispatch, history, location.pathname, prev]);

  return null;
};
